import { html, nothing, type TemplateResult } from 'lit';
import { formatFieldValue, type FieldChange } from '../../features/information-extraction/db/field-changes.js';
import type {
  AbstractMailExtraction,
  Correction,
  CorrectionKind,
  CorrectionStatus,
} from '../../features/information-extraction/db/MailExtractionResult.js';

/**
 * Everything a person changed about this request — Firma, Ansprechpartner und Positionen — in
 * one card that is always there.
 *
 * Corrections used to be scattered: a green box inside the position's edit form, nothing at
 * all for the header, so reading them meant opening each part of the request in turn. They
 * live in exactly one place now, and the card stays even when it is empty — "wurde hier etwas
 * von Hand geändert?" should be answered without going looking for the answer.
 */

const KIND_ICONS: Record<CorrectionKind, string> = {
  company: 'business',
  contact: 'person',
  product: 'inventory_2',
};

const STATUS_BADGES: Record<CorrectionStatus, { label: string; class: string }> = {
  changed: { label: 'Geändert', class: 'badge-success' },
  added: { label: 'Neu', class: 'badge-info' },
  removed: { label: 'Entfernt', class: 'badge-ghost' },
};

/** One field: what the extraction read, struck through, and what it says now. */
const renderChange = (change: FieldChange) => html`
  <li class="flex flex-wrap items-center gap-2">
    <span class="min-w-32 font-medium text-base-content/80">${change.label}</span>
    <span class="text-error/70 line-through">${formatFieldValue(change.from)}</span>
    <x-icon class="text-xs text-base-content/50">arrow_forward</x-icon>
    <span class="font-medium text-success">${formatFieldValue(change.to)}</span>
  </li>
`;

const renderCorrection = (correction: Correction) => html`
  <li class="px-5 py-3">
    <div class="flex flex-wrap items-center gap-2">
      <x-icon class="text-sm text-base-content/40">${KIND_ICONS[correction.kind]}</x-icon>
      <span class="text-sm font-semibold ${correction.status === 'removed' ? 'line-through' : ''}">
        ${correction.title}
      </span>
      ${correction.subtitle
        ? html`<span class="min-w-0 truncate text-xs text-base-content/50">${correction.subtitle}</span>`
        : nothing}
      <span class="badge badge-xs ${STATUS_BADGES[correction.status].class}">
        ${STATUS_BADGES[correction.status].label}
      </span>
    </div>
    ${correction.changes.length
      ? html`<ul class="mt-1.5 space-y-1 pl-6 text-sm">${correction.changes.map(renderChange)}</ul>`
      : nothing}
  </li>
`;

export const CorrectionsCard = (mailProcessing: AbstractMailExtraction): TemplateResult => {
  const corrections = mailProcessing.getCorrections();
  return html`
    <section class="rounded-2xl bg-base-100 border border-base-200 shadow-sm overflow-hidden">
      <div class="flex items-center gap-2.5 border-b border-base-200 px-5 py-4">
        <x-icon class="text-base-content/70">edit_note</x-icon>
        <h2 class="text-base font-bold tracking-tight text-base-content">Korrekturen</h2>
        <span class="badge badge-neutral badge-sm">${corrections.length}</span>
        <span class="truncate text-xs text-base-content/45">gegenüber der KI-Extraktion</span>
      </div>
      ${corrections.length === 0
        ? html`<div class="px-5 py-4 text-sm text-base-content/45">
            Nichts von Hand geändert — das Angebot steht so, wie die Extraktion es gelesen hat.
          </div>`
        : html`<ul class="divide-y divide-base-200">${corrections.map(renderCorrection)}</ul>`}
    </section>
  `;
};
